import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Calendar, Users, Search, Sparkles, Star, Wifi, Utensils, Waves, Dumbbell, Wine, PawPrint, Baby, TrendingDown } from "lucide-react";
import { SearchAutocomplete, Suggestion } from "@/components/SearchAutocomplete";
import { DatePickerModal } from "@/components/DatePickerModal";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { GuestPicker } from "@/components/GuestPicker";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import debounce from "lodash.debounce";
import { DateRange } from "react-day-picker";
import { format, differenceInDays } from "date-fns";

const trendingDestinations = [
  { 
    name: "Dubai, UAE", 
    region: "dubai-united-arab-emirates",
    description: "Opulent luxury amid futuristic skylines",
    hotels: "2,450+ hotels",
    image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&h=300&fit=crop"
  },
  { 
    name: "Paris, France", 
    region: "paris-france",
    description: "Timeless elegance and haute couture",
    hotels: "1,890+ hotels",
    image: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&h=300&fit=crop"
  },
  { 
    name: "London, UK", 
    region: "london-united-kingdom",
    description: "Royal heritage meets modern sophistication",
    hotels: "2,100+ hotels",
    image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&h=300&fit=crop"
  },
  { 
    name: "New York, USA", 
    region: "new-york-united-states",
    description: "Iconic skyline and world-class hospitality",
    hotels: "1,650+ hotels",
    image: "https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=400&h=300&fit=crop"
  },
  { 
    name: "Tokyo, Japan", 
    region: "tokyo-japan",
    description: "Where tradition meets cutting-edge luxury",
    hotels: "1,320+ hotels",
    image: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&h=300&fit=crop"
  },
  { 
    name: "Maldives", 
    region: "maldives",
    description: "Paradise islands and overwater villas",
    hotels: "850+ hotels",
    image: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=400&h=300&fit=crop"
  },
];

const quickAmenities = [
  { id: 'free_wifi', label: 'Free WiFi', icon: Wifi },
  { id: 'restaurant', label: 'Restaurant', icon: Utensils },
  { id: 'pool', label: 'Pool', icon: Waves },
  { id: 'fitness_center', label: 'Gym', icon: Dumbbell },
  { id: 'bar', label: 'Bar', icon: Wine },
  { id: 'pets_allowed', label: 'Pet Friendly', icon: PawPrint },
  { id: 'family_friendly', label: 'Family', icon: Baby },
];

interface LuxurySearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function LuxurySearchModal({ open, onOpenChange }: LuxurySearchModalProps) {
  const navigate = useNavigate();
  const [destination, setDestination] = useState("");
  const [selectedRegion, setSelectedRegion] = useState<Suggestion | null>(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [activeField, setActiveField] = useState<'destination' | 'dates' | 'guests' | null>(null);
  const [guests, setGuests] = useState({ 
    adults: 2, 
    children: 0, 
    rooms: 1,
    childAges: [] as number[]
  });
  const [isSearching, setIsSearching] = useState(false);
  const destinationInputRef = useRef<HTMLDivElement>(null);
  const [hoveredDestination, setHoveredDestination] = useState<string | null>(null);
  
  // Quick filters
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState<number[]>([0, 1000]);
  const [minStarRating, setMinStarRating] = useState<number>(0);
  const [flexibleDates, setFlexibleDates] = useState(false);

  const debouncedSearch = debounce(() => {
    if (!destination.trim()) {
      toast.error("Please enter a destination");
      setIsSearching(false);
      return;
    }

    if (!selectedRegion?.regionId) {
      toast.error("Please select a valid destination from the suggestions");
      setIsSearching(false);
      return;
    }

    if (!dateRange?.from || !dateRange?.to) {
      toast.error("Please select check-in and check-out dates");
      setIsSearching(false);
      return;
    }

    if (guests.adults < 1) {
      toast.error("At least 1 adult is required");
      setIsSearching(false);
      return;
    }

    const queryParams = new URLSearchParams({
      destination,
      regionId: String(selectedRegion.regionId),
      checkIn: dateRange.from.toISOString().split('T')[0],
      checkOut: dateRange.to.toISOString().split('T')[0],
      adults: guests.adults.toString(),
      children: guests.children.toString(),
      rooms: guests.rooms.toString(),
    });

    if (selectedAmenities.length > 0) {
      queryParams.set('amenities', selectedAmenities.join(','));
    }
    if (priceRange[0] > 0) queryParams.set('minPrice', priceRange[0].toString());
    if (priceRange[1] < 1000) queryParams.set('maxPrice', priceRange[1].toString());
    if (minStarRating > 0) queryParams.set('minRating', minStarRating.toString());

    // Open search results in a new tab instead of navigating away
    window.open(`/stays?${queryParams.toString()}`, '_blank');
    onOpenChange(false);
    setIsSearching(false);
  }, 300);

  const handleSearch = () => {
    setIsSearching(true);
    debouncedSearch();
  };

  const handleTrendingClick = (destination: { name: string; region: string }) => {
    setDestination(destination.name);
    setSelectedRegion({ 
      type: 'city', 
      name: destination.name, 
      regionId: destination.region as any
    });
    setActiveField(null);
  };

  const handleDateApply = () => {
    setShowDatePicker(false);
  };

  const getDestinationValue = () => {
    if (destination) return destination;
    return undefined;
  };

  const getDateValue = () => {
    if (dateRange?.from && dateRange?.to) {
      return `${format(dateRange.from, "MMM dd")} - ${format(dateRange.to, "MMM dd, yyyy")} • ${differenceInDays(dateRange.to, dateRange.from)} nights`;
    }
    return undefined;
  };

  const getGuestValue = () => {
    return `${guests.adults} ${guests.adults === 1 ? 'Adult' : 'Adults'}${guests.children > 0 ? `, ${guests.children} ${guests.children === 1 ? 'Child' : 'Children'}` : ''} • ${guests.rooms} ${guests.rooms === 1 ? 'Room' : 'Rooms'}`;
  };

  const toggleAmenity = (amenityId: string) => {
    setSelectedAmenities(prev => 
      prev.includes(amenityId) 
        ? prev.filter(id => id !== amenityId)
        : [...prev, amenityId]
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent 
          className="max-w-[90vw] lg:max-w-5xl border-gold/20 bg-gradient-to-b from-charcoal/98 to-black/98 backdrop-blur-2xl p-0 gap-0 overflow-hidden shadow-2xl shadow-gold/20"
          aria-describedby="search-description"
        >
          <DialogTitle className="sr-only">Search Luxury Hotels</DialogTitle>
          <div id="search-description" className="sr-only">
            Search for luxury hotels worldwide by destination, dates, and guests
          </div>

          {/* Gradient Glow Background */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-gold/10 blur-[120px] rounded-full" />
          </div>

          <div className="relative">
            {/* Header */}
            <div className="px-8 pt-10 pb-8 text-center space-y-4 border-b border-ivory/5">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-gold/10 px-4 py-1.5 text-xs text-gold backdrop-blur"
              >
                <Sparkles className="h-3.5 w-3.5" />
                Luxury Hotel Search
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="text-5xl font-light tracking-tight text-ivory"
              >
                Where to?
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="text-ivory/60 text-sm"
              >
                Search from thousands of luxury hotels worldwide
              </motion.p>
            </div>

            {/* Search Fields */}
            <div className="px-8 py-8 space-y-4">
              {/* Destination Field - Always visible with inline autocomplete */}
              <div className="space-y-2">
                <label className="block text-xs uppercase tracking-widest text-ivory/50 font-medium">
                  Destination
                </label>
                <div 
                  ref={destinationInputRef}
                  className="relative"
                >
                  <div 
                    className={`relative flex items-center gap-3 px-5 h-16 rounded-2xl border transition-all duration-300 bg-black/40 ${
                      activeField === 'destination' 
                        ? 'border-gold/50 shadow-[0_0_30px_rgba(212,175,55,0.15)]' 
                        : 'border-ivory/10 hover:border-ivory/20'
                    }`}
                    onClick={() => setActiveField('destination')}
                  >
                    <MapPin className="h-5 w-5 text-gold flex-shrink-0" />
                    <div className="flex-1">
                      <SearchAutocomplete
                        value={destination}
                        onChange={setDestination}
                        onSelectSuggestion={(s) => {
                          setSelectedRegion(s);
                          setActiveField(null);
                        }}
                        placeholder="Search cities, hotels, or destinations..."
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Date & Guests Grid */}
              <AnimatePresence>
                {activeField !== 'destination' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="grid grid-cols-2 gap-4"
                  >
                    {/* Dates Field */}
                    <div className="space-y-2">
                      <label className="block text-xs uppercase tracking-widest text-ivory/50 font-medium">
                        Dates
                      </label>
                      <button
                        onClick={() => {
                          setShowDatePicker(true);
                          setActiveField('dates');
                        }}
                        className={`w-full flex items-center gap-3 px-5 h-16 rounded-2xl border transition-all duration-300 text-left bg-black/40 ${
                          activeField === 'dates' || showDatePicker
                        ? 'border-gold/50 shadow-[0_0_30px_rgba(212,175,55,0.15)]'
                        : 'border-ivory/10 hover:border-ivory/20'
                    }`}
                  >
                    <Calendar className="h-5 w-5 text-gold flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${getDateValue() ? 'text-ivory' : 'text-ivory/50'}`}>
                        {getDateValue() || "Select dates"}
                      </div>
                    </div>
                  </button>
                </div>

                {/* Guests Field */}
                <div className="space-y-2">
                  <label className="block text-xs uppercase tracking-widest text-ivory/50 font-medium">
                    Guests
                  </label>
                  <Popover open={showGuestPicker} onOpenChange={setShowGuestPicker}>
                    <PopoverTrigger asChild>
                      <button
                        onClick={() => {
                          setShowGuestPicker(true);
                          setActiveField('guests');
                        }}
                        className={`w-full flex items-center gap-3 px-5 h-16 rounded-2xl border transition-all duration-300 text-left bg-black/40 ${
                          activeField === 'guests' || showGuestPicker
                            ? 'border-gold/50 shadow-[0_0_30px_rgba(212,175,55,0.15)]'
                            : 'border-ivory/10 hover:border-ivory/20'
                        }`}
                      >
                        <Users className="h-5 w-5 text-gold flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-ivory truncate">
                            {getGuestValue()}
                          </div>
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-96 p-0 border-gold/20 bg-charcoal/98 backdrop-blur-xl shadow-2xl z-[100000]"
                      align="start"
                      sideOffset={8}
                    >
                      <div className="p-6">
                        <GuestPicker
                          guests={guests}
                          onChange={(g) => setGuests({ ...g, childAges: g.childAges || [] })}
                          onClose={() => {
                            setShowGuestPicker(false);
                            setActiveField(null);
                          }}
                        />
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </motion.div>
                )}
              </AnimatePresence>

              {/* Quick Amenities */}
              <div className="space-y-3 pt-2">
                <label className="block text-xs uppercase tracking-widest text-ivory/50 font-medium">
                  Quick Filters
                </label>
                <div className="flex flex-wrap gap-2">
                  {quickAmenities.map((amenity) => {
                    const Icon = amenity.icon;
                    const isSelected = selectedAmenities.includes(amenity.id);
                    return (
                      <motion.button
                        key={amenity.id}
                        onClick={() => toggleAmenity(amenity.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className={`px-4 py-2.5 rounded-xl border transition-all duration-300 flex items-center gap-2 text-xs font-medium ${
                          isSelected
                            ? 'bg-gold/20 border-gold/50 text-gold shadow-lg shadow-gold/10'
                            : 'bg-black/30 border-ivory/10 text-ivory/70 hover:border-gold/30 hover:bg-black/40'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {amenity.label}
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              {/* Star Rating */}
              <div className="space-y-3">
                <label className="block text-xs uppercase tracking-widest text-ivory/50 font-medium">
                  Minimum Star Rating
                </label>
                <div className="flex gap-2">
                  {[3, 4, 5].map((rating) => (
                    <motion.button
                      key={rating}
                      onClick={() => setMinStarRating(minStarRating === rating ? 0 : rating)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className={`flex-1 px-4 py-3 rounded-xl border transition-all duration-300 ${
                        minStarRating === rating
                          ? 'bg-gold/20 border-gold/50 text-gold shadow-lg shadow-gold/10'
                          : 'bg-black/30 border-ivory/10 text-ivory/70 hover:border-gold/30 hover:bg-black/40'
                      }`}
                    >
                      <div className="flex items-center justify-center gap-1">
                        {Array.from({ length: rating }).map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-current" />
                        ))}
                      </div>
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-xs uppercase tracking-widest text-ivory/50 font-medium">
                    Price Range per Night
                  </label>
                  <div className="flex items-center gap-2 text-sm text-ivory">
                    <TrendingDown className="h-4 w-4 text-gold" />
                    <span className="font-medium">${priceRange[0]} - ${priceRange[1]}+</span>
                  </div>
                </div>
                <Slider
                  min={0}
                  max={1000}
                  step={50}
                  value={priceRange}
                  onValueChange={setPriceRange}
                  className="w-full"
                />
              </div>

              {/* Search Button */}
              <button
                onClick={handleSearch}
                disabled={isSearching}
                className="w-full h-14 px-8 rounded-2xl bg-gradient-to-r from-gold via-gold-glow to-gold text-charcoal font-semibold text-base shadow-lg shadow-gold/25 hover:shadow-xl hover:shadow-gold/35 hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
              >
                <Search className="h-5 w-5" />
                <span>{isSearching ? 'Searching...' : 'Search Hotels'}</span>
              </button>
            </div>

            {/* Trending Destinations */}
            <div className="px-8 pb-8 pt-4 border-t border-ivory/5">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-1 w-1 rounded-full bg-gold/60 animate-pulse" />
                <span className="text-xs uppercase tracking-widest text-ivory/40 font-medium">
                  Popular Destinations
                </span>
              </div>
              <div className="relative">
                <div className="flex flex-wrap gap-2">
                  {trendingDestinations.map((dest) => (
                    <div key={dest.region} className="relative">
                      <motion.button
                        onClick={() => handleTrendingClick(dest)}
                        onMouseEnter={() => setHoveredDestination(dest.region)}
                        onMouseLeave={() => setHoveredDestination(null)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="px-4 py-2.5 rounded-xl border border-ivory/10 bg-black/30 hover:border-gold/30 hover:bg-black/40 text-xs text-ivory/70 hover:text-ivory transition-all duration-300 flex items-center gap-2 relative z-10"
                      >
                        <MapPin className="h-3.5 w-3.5 text-gold/70" />
                        {dest.name}
                      </motion.button>
                      
                      {/* Hover Preview Card */}
                      <AnimatePresence>
                        {hoveredDestination === dest.region && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            transition={{ duration: 0.2 }}
                            className="absolute bottom-full left-0 mb-3 w-80 pointer-events-none z-[100]"
                          >
                            <div className="bg-charcoal/98 backdrop-blur-xl border border-gold/20 rounded-2xl overflow-hidden shadow-2xl shadow-gold/10">
                              {/* Image */}
                              <div className="relative h-40 overflow-hidden">
                                <img 
                                  src={dest.image} 
                                  alt={dest.name}
                                  className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/20 to-transparent" />
                                
                                {/* Hotel Count Badge */}
                                <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-gold/90 backdrop-blur-sm">
                                  <span className="text-xs font-semibold text-charcoal">
                                    {dest.hotels}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Content */}
                              <div className="p-4 space-y-2">
                                <h4 className="text-ivory font-semibold text-base">
                                  {dest.name}
                                </h4>
                                <p className="text-ivory/60 text-sm leading-relaxed">
                                  {dest.description}
                                </p>
                                
                                {/* Features */}
                                <div className="flex items-center gap-4 pt-2 text-xs text-ivory/50">
                                  <div className="flex items-center gap-1">
                                    <Star className="h-3.5 w-3.5 text-gold fill-gold" />
                                    <span>Premium</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Sparkles className="h-3.5 w-3.5 text-gold" />
                                    <span>Luxury</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Arrow pointing down */}
                            <div className="absolute -bottom-2 left-4 w-4 h-4 bg-charcoal/98 border-r border-b border-gold/20 transform rotate-45" />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Date Picker Modal */}
      <AnimatePresence>
        {showDatePicker && (
          <DatePickerModal
            open={showDatePicker}
            onOpenChange={(open) => {
              setShowDatePicker(open);
              if (!open) setActiveField(null);
            }}
            value={dateRange}
            onChange={setDateRange}
            onApply={() => {
              setShowDatePicker(false);
              setActiveField(null);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
